import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CircularProgress,
  Popover,
  Paper,
  TextField,
  MenuItem,
  Select
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import moment from "moment";
import { useSnackbar } from "notistack";
import { NoRecordFound } from "../shared_elements";
import { backendDateFormat, fieldDateFormat } from "../constants";
import { checkApiStatus } from "../utils";
import { globalGetService } from "../utils/globalApiServices";
import AiChatBrand from "./AiChatBrand";
import { MockApi } from "./api/mockApi";
import RequestCard from "./RequestCard";
import TokenAdminAccess from "../hoc/TokenAdminAccess";
import { trackActivity } from "../utils/mixpanel";
import { useAiConsent } from "./AiConsentGate";
import { isConsentCheckCandidate } from "./consentConstants";
import "./tokenManagement.css";

const COMMENTS_MAX = 1000;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const USER_DROPDOWN_API = "/console/obligations/user_dropdown/";

const statusOptions = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" }
];

const emptyFilter = {
  status: "",
  user_id: "",
  created_at: ""
};

function getUserDisplayName(user) {
  if (!user) return "";
  const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  return fullName || user.name || user.email || String(user.id ?? "");
}

function isPendingStatus(status) {
  return String(status || "").toLowerCase() === "pending";
}

function cleanFilter(nextFilter) {
  return Object.fromEntries(
    Object.entries(nextFilter).filter(([, value]) => String(value || "").trim() !== "")
  );
}

export function TokenManagement() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { accepted: consentAccepted, requestReconsent } = useAiConsent();
  const filterDebounceRef = useRef(null);
  const [skeletonLoader, setSkeletonLoader] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState(emptyFilter);
  const [queryConfig, setQueryConfig] = useState({
    page: 1,
    per_page: DEFAULT_PAGE_SIZE
  });
  const [requestsData, setRequestsData] = useState({
    list: [],
    pagination: {
      page: 1,
      per_page: DEFAULT_PAGE_SIZE,
      total: 0,
      total_pages: 1
    }
  });
  const [reviewState, setReviewState] = useState({
    requestId: null,
    action: null,
    comments: "",
    submitting: false
  });
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    if (!consentAccepted) {
      setSkeletonLoader(false);
      return undefined;
    }
    trackActivity("Page Viewed", { page_title: "Token Requests" });
    loadRequests(
      {
        page: 1,
        page_size: DEFAULT_PAGE_SIZE
      },
      "skeletonLoader"
    );
    loadUserOptions();
    return () => {
      if (filterDebounceRef.current) window.clearTimeout(filterDebounceRef.current);
    };
  }, [consentAccepted]);

  async function loadUserOptions() {
    setUsersLoading(true);
    try {
      const response = await globalGetService(USER_DROPDOWN_API);
      if (checkApiStatus(response)) {
        const list = response?.data?.data;
        setUserOptions(Array.isArray(list) ? list : []);
      } else {
        setUserOptions([]);
      }
    } catch {
      setUserOptions([]);
    } finally {
      setUsersLoading(false);
    }
  }

  async function loadRequests(query = {}, loaderType = "pageLoader") {
    if (loaderType === "skeletonLoader") setSkeletonLoader(true);
    else setIsLoading(true);

    try {
      const response = await MockApi.loadAllUsersTokenRechargeRequests(query);
      setRequestsData(response);
      setQueryConfig({
        page: response.pagination?.page || query.page || 1,
        per_page:
          response.pagination?.per_page ||
          query.page_size ||
          DEFAULT_PAGE_SIZE
      });
    } catch (error) {
      setRequestsData({
        list: [],
        pagination: {
          page: 1,
          per_page: query.page_size || DEFAULT_PAGE_SIZE,
          total: 0,
          total_pages: 1
        }
      });
      if (!(isConsentCheckCandidate(error) && (await requestReconsent(error)))) {
        enqueueSnackbar(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load token recharge requests.",
          { variant: "error" }
        );
      }
    } finally {
      setSkeletonLoader(false);
      setIsLoading(false);
    }
  }

  const refreshCurrentList = () =>
    loadRequests({
      ...cleanFilter(filter),
      page: queryConfig.page || 1,
      page_size: queryConfig.per_page || DEFAULT_PAGE_SIZE
    });

  const applyFilterNow = (nextFilter) => {
    setReviewState({
      requestId: null,
      action: null,
      comments: "",
      submitting: false
    });
    loadRequests({
      ...cleanFilter(nextFilter),
      page: 1,
      page_size: queryConfig.per_page || DEFAULT_PAGE_SIZE
    });
  };

  const updateFilter = (key, value) => {
    const nextFilter = { ...filter, [key]: value };
    setFilter(nextFilter);
    handleItemClicked(`Filter: ${key}`);

    if (filterDebounceRef.current) window.clearTimeout(filterDebounceRef.current);
    applyFilterNow(nextFilter);
  };

  const handleItemClicked = (page_type) => {
    trackActivity("Item Clicked", {
      page_title: "Token Requests",
      page_type
    });
  };

  const closeFilterPopover = () => {
    setOpenFilterKey(null);
    setFilterAnchorEl(null);
    setUserSearch("");
  };

  const clearFilters = () => {
    if (filterDebounceRef.current) window.clearTimeout(filterDebounceRef.current);
    setFilter(emptyFilter);
    closeFilterPopover();
    handleItemClicked("Clear Filters");
    applyFilterNow(emptyFilter);
  };

  const openFilterPopover = (key, event) => {
    const anchor =
      event.currentTarget.closest(".filter-chip") || event.currentTarget;
    setOpenFilterKey(key);
    setFilterAnchorEl(anchor);
    if (key === "user_id") {
      setUserSearch("");
    }
  };

  const clearSingleFilter = (key, event) => {
    event.stopPropagation();
    if (filterDebounceRef.current) window.clearTimeout(filterDebounceRef.current);
    if (openFilterKey === key) closeFilterPopover();
    updateFilter(key, "");
  };

  const hasActiveFilters = Object.values(filter).some((value) => String(value || "").trim());

  const statusLabel =
    statusOptions.find((option) => option.value === (filter.status || ""))?.label ||
    "All";

  const selectedUser =
    userOptions.find(
      (user) => String(user?.id ?? "") === String(filter.user_id || "")
    ) || null;

  const selectedUserLabel = selectedUser
    ? getUserDisplayName(selectedUser)
    : filter.user_id
      ? String(filter.user_id)
      : null;

  const filteredUsers = userOptions.filter((user) => {
    const haystack = `${getUserDisplayName(user)} ${user?.email || ""}`.toLowerCase();
    return haystack.includes(userSearch.trim().toLowerCase());
  });

  const formatCreatedAtLabel = (value) => {
    if (!value) return null;
    const parsed = moment(value, backendDateFormat, true);
    return parsed.isValid() ? parsed.format(fieldDateFormat) : value;
  };

  const openReviewForm = (request, action) => {
    setReviewState({
      requestId: request.id,
      action,
      comments: "",
      submitting: false
    });
  };

  const closeReviewForm = () => {
    if (reviewState.submitting) return;
    setReviewState({
      requestId: null,
      action: null,
      comments: "",
      submitting: false
    });
  };

  const submitReview = async () => {
    const { action, requestId, comments } = reviewState;
    if (!action || !requestId) return;

    if (!comments.trim()) {
      enqueueSnackbar(
        action === "approved"
          ? "Comments are required to approve a request."
          : "Comments are required to reject a request.",
        { variant: "error" }
      );
      return;
    }

    setReviewState((current) => ({ ...current, submitting: true }));
    try {
      const response = await MockApi.reviewTokenRechargeRequest(requestId, {
        status: action,
        comments: comments.trim()
      });
      enqueueSnackbar(
        response?.message ||
          (action === "approved"
            ? "Token request approved."
            : "Token request rejected."),
        { variant: "success" }
      );
      trackActivity("Item Edited", {
        page_title: "Token Requests",
        success_msg:
          response?.message ||
          (action === "approved"
            ? "Token request approved."
            : "Token request rejected."),
        item_id: requestId,
        payload: { status: action }
      });
      setReviewState({
        requestId: null,
        action: null,
        comments: "",
        submitting: false
      });
      await refreshCurrentList();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          `Unable to ${action.toLowerCase()} this request.`,
        { variant: "error" }
      );
      trackActivity("Item Edited", {
        page_title: "Token Requests",
        error_msg:
          error?.response?.data?.message ||
          error?.message ||
          `Unable to ${action.toLowerCase()} this request.`,
        item_id: requestId,
        payload: { status: action }
      });
      setReviewState((current) => ({
        ...current,
        submitting: false
      }));
    }
  };

  return (
    <section className="active-asset-section token-management-page">
      <div className="token-page-header">
        <AiChatBrand />
        <button
          type="button"
          className="token-back-link"
          onClick={() => {
            handleItemClicked("Back to AI Chat");
            navigate("/ai-chat");
          }}
        >
          <ArrowBackIcon fontSize="inherit" />
          Back to AI Chat
        </button>
      </div>

      <Paper className="token-request-panel" square>
        <div className="request-history">
          <div className="requests-header-row">
            <h3>Requests</h3>

            <div className="filter-toolbar">
            <span className="filter-toolbar-icon" aria-hidden="true">
              <FilterListIcon fontSize="small" />
            </span>

            <div
              className={`filter-chip${filter.status ? " is-active" : ""}${
                openFilterKey === "status" ? " is-open" : ""
              }`}
            >
              <button
                type="button"
                className="filter-chip-btn"
                onClick={(event) => openFilterPopover("status", event)}
              >
                <span className="filter-chip-label">Status</span>
                {filter.status ? (
                  <span className={`filter-chip-value status-${filter.status}`}>
                    {statusLabel}
                  </span>
                ) : null}
                {!filter.status ? (
                  <KeyboardArrowDownIcon className="filter-chip-caret" />
                ) : null}
              </button>
              {filter.status ? (
                <button
                  type="button"
                  className="filter-chip-clear"
                  aria-label="Clear status filter"
                  onClick={(event) => clearSingleFilter("status", event)}
                >
                  <CloseIcon fontSize="inherit" />
                </button>
              ) : null}
            </div>

            <div
              className={`filter-chip${filter.user_id ? " is-active" : ""}${
                openFilterKey === "user_id" ? " is-open" : ""
              }`}
            >
              <button
                type="button"
                className="filter-chip-btn"
                onClick={(event) => openFilterPopover("user_id", event)}
              >
                <span className="filter-chip-label">Requested by</span>
                {selectedUserLabel ? (
                  <span className="filter-chip-value">{selectedUserLabel}</span>
                ) : null}
                {!filter.user_id ? (
                  <KeyboardArrowDownIcon className="filter-chip-caret" />
                ) : null}
              </button>
              {filter.user_id ? (
                <button
                  type="button"
                  className="filter-chip-clear"
                  aria-label="Clear requested by filter"
                  onClick={(event) => clearSingleFilter("user_id", event)}
                >
                  <CloseIcon fontSize="inherit" />
                </button>
              ) : null}
            </div>

            <div
              className={`filter-chip${filter.created_at ? " is-active" : ""}${
                openFilterKey === "created_at" ? " is-open" : ""
              }`}
            >
              <button
                type="button"
                className="filter-chip-btn"
                onClick={(event) => openFilterPopover("created_at", event)}
              >
                <span className="filter-chip-label">Created On</span>
                {filter.created_at ? (
                  <span className="filter-chip-value">
                    {formatCreatedAtLabel(filter.created_at)}
                  </span>
                ) : null}
                {!filter.created_at ? (
                  <KeyboardArrowDownIcon className="filter-chip-caret" />
                ) : null}
              </button>
              {filter.created_at ? (
                <button
                  type="button"
                  className="filter-chip-clear"
                  aria-label="Clear created on filter"
                  onClick={(event) => clearSingleFilter("created_at", event)}
                >
                  <CloseIcon fontSize="inherit" />
                </button>
              ) : null}
            </div>

            {hasActiveFilters ? (
              <button
                type="button"
                className="filter-clear-btn"
                onClick={clearFilters}
              >
                Clear
              </button>
            ) : null}

            <Popover
              open={Boolean(openFilterKey) && Boolean(filterAnchorEl)}
              anchorEl={filterAnchorEl}
              onClose={closeFilterPopover}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{
                paper: {
                  className: `filter-popover-paper${
                    openFilterKey === "status" ? " filter-popover-status" : ""
                  }${openFilterKey === "user_id" ? " filter-popover-lessor" : ""}`,
                  style:
                    openFilterKey === "status" && filterAnchorEl
                      ? { width: filterAnchorEl.offsetWidth, minWidth: filterAnchorEl.offsetWidth }
                      : openFilterKey === "user_id"
                        ? { minWidth: 280 }
                        : undefined
                }
              }}
            >
              {openFilterKey === "status" ? (
                <div className="filter-status-options">
                  {statusOptions.map((option) => (
                    <button
                      type="button"
                      key={option.value || "all"}
                      className={`filter-status-pill${
                        option.value ? ` status-${option.value}` : " status-all"
                      }${
                        (filter.status || "") === option.value ? " is-selected" : ""
                      }`}
                      onClick={() => {
                        updateFilter("status", option.value);
                        closeFilterPopover();
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : null}

              {openFilterKey === "user_id" ? (
                <div className="filter-popover-form filter-popover-lessor-form">
                  {usersLoading ? (
                    <div className="filter-lessor-loading">
                      <CircularProgress size={20} />
                      <span>Loading users…</span>
                    </div>
                  ) : userOptions.length ? (
                    <>
                      <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        value={userSearch}
                        onChange={(event) => setUserSearch(event.target.value)}
                        placeholder="Search requested by"
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                      {filteredUsers.length ? (
                        <div className="filter-lessor-options">
                          {filteredUsers.map((user) => (
                            <button
                              type="button"
                              key={user.id}
                              className={`filter-lessor-option${
                                String(filter.user_id) === String(user.id)
                                  ? " is-selected"
                                  : ""
                              }`}
                              onClick={() => {
                                updateFilter("user_id", String(user.id));
                                closeFilterPopover();
                              }}
                            >
                              <span className="filter-user-option">
                                <span className="filter-user-option-name">
                                  {getUserDisplayName(user)}
                                </span>
                                {user?.email ? (
                                  <span className="filter-user-option-email">
                                    {user.email}
                                  </span>
                                ) : null}
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="filter-lessor-empty">No matching users</div>
                      )}
                    </>
                  ) : (
                    <div className="filter-lessor-empty">No users available</div>
                  )}
                </div>
              ) : null}

              {openFilterKey === "created_at" ? (
                <div className="filter-popover-form filter-date-form">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      label="Created On"
                      format={fieldDateFormat}
                      value={
                        filter.created_at
                          ? moment(filter.created_at, backendDateFormat)
                          : null
                      }
                      onChange={(data) => {
                        const nextValue =
                          data && moment(data).isValid()
                            ? moment(data).format(backendDateFormat)
                            : "";
                        updateFilter("created_at", nextValue);
                        closeFilterPopover();
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          InputLabelProps: { shrink: true }
                        },
                        field: {
                          clearable: true,
                          onClear: () => {
                            updateFilter("created_at", "");
                            closeFilterPopover();
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </div>
              ) : null}
            </Popover>
          </div>
          </div>

          <div className="request-list-scroll">
            {skeletonLoader || isLoading ? (
              <div className="modal-loading">Loading requests…</div>
            ) : requestsData?.list?.length ? (
              <div className="request-list">
                {requestsData.list.map((item) => {
                  const isReviewing =
                    reviewState.requestId === item.id && Boolean(reviewState.action);
                  const isApprove = reviewState.action === "approved";

                  return (
                    <RequestCard
                      key={item.id}
                      item={item}
                      actions={
                        isPendingStatus(item.status) && !isReviewing ? (
                          <>
                            <button
                              type="button"
                              className="approve-request-button"
                              disabled={reviewState.submitting}
                              onClick={() => {
                                handleItemClicked("Approve");
                                openReviewForm(item, "approved");
                              }}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="cancel-request-button"
                              disabled={reviewState.submitting}
                              onClick={() => {
                                handleItemClicked("Reject");
                                openReviewForm(item, "rejected");
                              }}
                            >
                              Reject
                            </button>
                          </>
                        ) : null
                      }
                      form={
                        isReviewing ? (
                          <div className="cancel-request-form">
                            <label htmlFor={`review-comments-${item.id}`}>
                              {isApprove
                                ? "Approval comments"
                                : "Rejection comments"}
                            </label>
                            <textarea
                              id={`review-comments-${item.id}`}
                              value={reviewState.comments}
                              onChange={(event) =>
                                setReviewState((current) => ({
                                  ...current,
                                  comments: event.target.value.slice(
                                    0,
                                    COMMENTS_MAX
                                  )
                                }))
                              }
                              placeholder={
                                isApprove
                                  ? "Enter approval comments"
                                  : "Enter the reason for rejecting this request"
                              }
                              maxLength={COMMENTS_MAX}
                              rows={3}
                            />
                            <div className="review-comments-count">
                              {reviewState.comments.length}/{COMMENTS_MAX}
                            </div>
                            <div className="cancel-request-actions">
                              <button
                                type="button"
                                className="secondary-action"
                                disabled={Boolean(reviewState.submitting)}
                                onClick={closeReviewForm}
                              >
                                Keep request
                              </button>
                              <button
                                type="button"
                                className={
                                  isApprove
                                    ? "approve-confirm-button"
                                    : "cancel-confirm-button"
                                }
                                disabled={
                                  Boolean(reviewState.submitting) ||
                                  !reviewState.comments.trim()
                                }
                                onClick={submitReview}
                              >
                                {reviewState.submitting
                                  ? isApprove
                                    ? "Approving…"
                                    : "Rejecting…"
                                  : isApprove
                                    ? "Confirm approval"
                                    : "Confirm rejection"}
                              </button>
                            </div>
                          </div>
                        ) : null
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <NoRecordFound />
            )}
          </div>
        </div>

        <div className="token-request-pagination">
          {(Number(requestsData?.pagination?.total || 0) > 0 ||
            Number(requestsData?.pagination?.total_pages || 0) > 1) && (
            <div className="request-pagination">
              <div className="page-size-control">
                <span className="page-size-label">Items per page</span>
                <Select
                  size="small"
                  className="page-size-select"
                  value={Number(
                    requestsData?.pagination?.per_page ||
                      queryConfig.per_page ||
                      DEFAULT_PAGE_SIZE
                  )}
                  disabled={isLoading || skeletonLoader}
                  onChange={(event) => {
                    const nextPageSize =
                      Number(event.target.value) || DEFAULT_PAGE_SIZE;
                    handleItemClicked("Items per page");
                    loadRequests({
                      ...cleanFilter(filter),
                      page: 1,
                      page_size: nextPageSize
                    });
                  }}
                  MenuProps={{
                    PaperProps: { className: "page-size-menu-paper" }
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="request-pagination-nav">
                <button
                  type="button"
                  disabled={
                    isLoading ||
                    skeletonLoader ||
                    Number(requestsData?.pagination?.page || 1) <= 1
                  }
                  onClick={() => {
                    handleItemClicked("Previous Page");
                    loadRequests({
                      ...cleanFilter(filter),
                      page: Number(requestsData?.pagination?.page || 1) - 1,
                      page_size:
                        requestsData?.pagination?.per_page ||
                        queryConfig.per_page ||
                        DEFAULT_PAGE_SIZE
                    });
                  }}
                >
                  Previous
                </button>
                <span>
                  Page {Number(requestsData?.pagination?.page || 1)} of{" "}
                  {Number(requestsData?.pagination?.total_pages || 1)}
                </span>
                <button
                  type="button"
                  disabled={
                    isLoading ||
                    skeletonLoader ||
                    Number(requestsData?.pagination?.page || 1) >=
                      Number(requestsData?.pagination?.total_pages || 1)
                  }
                  onClick={() => {
                    handleItemClicked("Next Page");
                    loadRequests({
                      ...cleanFilter(filter),
                      page: Number(requestsData?.pagination?.page || 1) + 1,
                      page_size:
                        requestsData?.pagination?.per_page ||
                        queryConfig.per_page ||
                        DEFAULT_PAGE_SIZE
                    });
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </Paper>
    </section>
  );
}

export default TokenAdminAccess(TokenManagement);
